import {
  Stack,
  Text,
  PrimaryButton,
  ButtonGroup,
  BasicButton,
} from "@certn/cascadia";
import {
  useStripe,
  useElements,
  PaymentElement,
  PaymentElementProps,
} from "@stripe/react-stripe-js";
import { PaymentMethodCreateParams } from "@stripe/stripe-js";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { object } from "yup";

import { fetcher, InferApplicantAPI } from "@certn/api";
import {
  useAllowStripe3dsOverlay,
  confirmSetupAndWaitForPaymentProfile,
} from "@certn/utils";
import { options } from "./constants";

interface Props {
  defaultAddress?: {
    country?: string;
    postal_code?: string;
  };
  handleClose?: () => void;
  /**
   * Total payment-profile count (API `count`, not page `results.length`)
   * before this add flow. Required so polling cannot treat an existing card
   * list as success when the baseline was omitted (DAT-1731).
   */
  initialPaymentProfileCount: number;
  /**
   * Called when the current SetupIntent is no longer usable (e.g. polling
   * timed out after confirmSetup succeeded). Parent should remint a fresh
   * SetupIntent so the user can try a different card (DAT-1731 Bugbot).
   */
  onSetupIntentConsumed?: (message: string) => void;
  initialErrorMessage?: string | null;
  clientSecret?: string | null;
}

export function getPaymentElementOptions(
  defaultAddress?: Props["defaultAddress"],
  readOnly = false
): PaymentElementProps["options"] {
  const country = defaultAddress?.country;
  return {
    ...options,
    readOnly,
    // Pre-fill address from personal_profile to ensure consistency with backend
    fields: {
      billingDetails: {
        address: "if_required",
      },
    },
    ...(country
      ? {
          defaultValues: {
            billingDetails: {
              address: {
                country,
                postal_code: defaultAddress?.postal_code,
              },
            },
          },
        }
      : {}),
  };
}

export function StripeSetupIntentForm(props: Props) {
  const {
    defaultAddress,
    handleClose,
    initialPaymentProfileCount,
    onSetupIntentConsumed,
    initialErrorMessage,
    clientSecret,
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<null | string>(
    initialErrorMessage ?? null
  );
  const [isSaving, setIsSaving] = useState(false);
  const isMountedRef = useRef(true);
  useAllowStripe3dsOverlay();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    if (!stripe || !elements) {
      setIsSaving(false);
      return;
    }

    const billingDetails: PaymentMethodCreateParams.BillingDetails = {
      address: {
        // For applicants, we pass None for title (line2) since title is auto-generated
        // This ensures the backend can safely access billing_details.address.line2
        line2: null,
      },
    };

    try {
      const paymentProfilesUrl =
        "/api/internal/applicant/me/payment-profiles/" as const;
      const fetchCount = async () => {
        const response = await fetcher.request<
          InferApplicantAPI<typeof paymentProfilesUrl>
        >(paymentProfilesUrl, { cache: "no-store" });
        return typeof response?.count === "number" ? response.count : undefined;
      };

      let baseline = initialPaymentProfileCount;
      const currentCount = await fetchCount().catch(() => undefined);
      if (typeof currentCount === "number") baseline = currentCount;

      const outcome = await confirmSetupAndWaitForPaymentProfile({
        stripe,
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: { billing_details: billingDetails },
        },
        clientSecret,
        fetchCount,
        baseline,
        isAborted: () => !isMountedRef.current,
      });

      if (!isMountedRef.current) return;

      if (outcome.status === "created") {
        handleClose?.();
        setIsSaving(false);
        return;
      }
      if (outcome.status === "confirm_error") {
        setErrorMessage(outcome.message || t("Something went wrong"));
        setIsSaving(false);
        return;
      }

      const rejectionMessage = t(
        "This card can't be used. Please try a different card."
      );
      if (onSetupIntentConsumed) {
        onSetupIntentConsumed(rejectionMessage);
        return;
      }
      setErrorMessage(rejectionMessage);
      setIsSaving(false);
    } catch {
      if (!isMountedRef.current) return;
      setErrorMessage(t("Something went wrong"));
      setIsSaving(false);
    }
  };

  const schema = object({});

  return (
    <Formik
      initialValues={{}}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {() => {
        return (
          <Form>
            <Stack gap="space-4">
              <PaymentElement
                options={getPaymentElementOptions(defaultAddress, isSaving)}
              />
              {errorMessage && (
                <Text style="form/body-secondary" color="text-danger">
                  {errorMessage}
                </Text>
              )}
              <ButtonGroup justify="end">
                <BasicButton
                  type="button"
                  size="medium"
                  onPress={handleClose}
                  testID="add-payment-profile-modal-close-button"
                >
                  {t("Cancel")}
                </BasicButton>
                <PrimaryButton
                  type="submit"
                  size="medium"
                  testID="add-payment-profile-modal-save-button"
                  isDisabled={isSaving}
                  isLoading={isSaving}
                >
                  {t("Save")}
                </PrimaryButton>
              </ButtonGroup>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
}
